import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerBody,
  DrawerFooter,
  DrawerCloseButton,
  Button,
  Text,
  Flex,
  useToast,
  Box,
  Center,
  Input,
  Alert,
  AlertIcon,
} from "@chakra-ui/react";
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";

import React, { useEffect, useState } from "react";
import { FiLock } from "react-icons/fi";
import CardConsentNote from "./cardConsentNote";
import { updateGuestEmail } from "../../Backend/Notifications/updateGuestEmail";

export const CreditCardDrawer = ({
  isOpen,
  onClose,
  clientSecret,
  handleWaiverCall,
  handlePayment: externalHandlePayment,
  waiverToastTitle = "Waiver Check Failed",
  waiverToastDescription = "Could not verify waiver after payment.",
  paymentType,
  upsellEmailFollowUp = false,
  guestId,
  bookingId,
  defaultEmail,
  onUpsellComplete,
  // Optional purchase summary shown above the card fields. All three are
  // optional so the deposit/waiver callers that pass none render as before.
  itemName,
  itemDuration,
  itemAmount,
}) => {
  const toast = useToast();
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [showUpsellFollowUp, setShowUpsellFollowUp] = useState(false);
  const [emailValue, setEmailValue] = useState("");
  const [emailError, setEmailError] = useState("");
  const [emailLoading, setEmailLoading] = useState(false);

  // Track completeness of each card field
  const [cardComplete, setCardComplete] = useState(false);
  const [expiryComplete, setExpiryComplete] = useState(false);
  const [cvcComplete, setCvcComplete] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setShowUpsellFollowUp(false);
      setEmailValue("");
      setEmailError("");
      setEmailLoading(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (showUpsellFollowUp && defaultEmail && !emailValue) {
      setEmailValue(defaultEmail);
    }
  }, [showUpsellFollowUp, defaultEmail, emailValue]);

  const isValidEmail = (value) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((value || "").trim());

  const handleUpsellComplete = () => {
    onClose();
    onUpsellComplete?.();
  };

  const handleUpdateEmail = async () => {
    const trimmedEmail = emailValue.trim();

    if (!isValidEmail(trimmedEmail)) {
      setEmailError("Please enter a valid email address.");
      return;
    }

    setEmailLoading(true);
    setEmailError("");

    try {
      await updateGuestEmail(guestId, trimmedEmail, bookingId);
      toast({
        title: "Email updated",
        description: "We'll send updates to this email when your request is approved.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      handleUpsellComplete();
    } catch (error) {
      console.error("Error updating email:", error);
      const errorMsg =
        error.response?.data?.message ||
        error.message ||
        "Failed to update email. Please try again.";
      setEmailError(errorMsg);
    } finally {
      setEmailLoading(false);
    }
  };

  const handleEmailKeyDown = (e) => {
    if (e.key === "Enter" && !emailLoading) {
      e.preventDefault();
      handleUpdateEmail();
    }
  };

  const internalHandlePayment = async () => {
    if (!stripe || !elements || !clientSecret) return;

    setLoading(true);

    const cardElement = elements.getElement(CardNumberElement);

    // Prevent payment if any field is incomplete
    if (!cardComplete || !expiryComplete || !cvcComplete) {
      toast({
        title: "Incomplete Card Info",
        description: "Please fill out all card fields.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      setLoading(false);
      return;
    }

    if (!cardElement) {
      toast({
        title: "Input Information",
        description: "Please input card information.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      setLoading(false);
      return;
    }

    // For damage waivers, check waiver first before charging
    if (paymentType === "waiver") {
      const waiverSuccess = await handleWaiverCall?.();
      if (!waiverSuccess) {
        toast({
          title: waiverToastTitle,
          description: waiverToastDescription,
          status: "error",
          duration: 4000,
          isClosable: true,
        });
        setLoading(false);
        return;
      }
    }
    
    const result = await stripe.confirmCardPayment(clientSecret, {
      payment_method: {
        card: cardElement,
      },
    });

    if (result.error) {
      toast({
        title: "Payment Failed",
        description: result.error.message,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      setLoading(false);
      return;
    }

    if (result.paymentIntent.status !== "succeeded") {
      toast({
        title: "Payment Not Completed",
        description: "Payment was not successful. Please try again.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      setLoading(false);
      return;
    }

    onClose();
    setLoading(false);
  };

  const handleClick = async () => {
    const showUpsellPaidStep = () => {
      if (upsellEmailFollowUp) {
        setShowUpsellFollowUp(true);
      }
    };

    if (!cardComplete || !expiryComplete || !cvcComplete) {
      toast({
        title: "Incomplete Card Info",
        description: "Please fill out all card fields.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    if (externalHandlePayment) {
      setLoading(true);
      const paymentSucceeded = await externalHandlePayment({
        stripe,
        elements,
        setLoading,
        onClose,
        toast,
        showUpsellPaidStep,
      });
      if (paymentSucceeded && upsellEmailFollowUp) {
        setShowUpsellFollowUp(true);
      }
      setLoading(false);
    } else {
      await internalHandlePayment();
    }
  };

  return (
    <Drawer
      isOpen={isOpen}
      placement="bottom"
      onClose={showUpsellFollowUp ? handleUpsellComplete : onClose}
      closeOnOverlayClick={!showUpsellFollowUp}
      closeOnEsc={!showUpsellFollowUp}
    >
      <DrawerOverlay />
      <DrawerContent
        minH="35vh"
        borderTopRadius="2xl"
        maxW={{ base: "100%", md: "520px" }}
        mx="auto"
        mb={{ md: 8 }}
        borderRadius={{ md: "24px" }}
      >
        <Flex mx={3} my={4} align="center" justify="space-between">
          <Text fontSize="18px" fontWeight={800}>
            {showUpsellFollowUp ? "Payment authorized" : "Pay With Card"}
          </Text>
          <DrawerCloseButton pos="relative" top={0} right={0} />
        </Flex>

        {showUpsellFollowUp ? (
          <>
            <DrawerBody p={3}>
              <Box
                bg="green.900"
                color="green.100"
                border="1px solid"
                borderColor="green.600"
                borderRadius="14px"
                p={4}
                mb={4}
              >
                <Text fontSize="15px" fontWeight={800} mb={1}>
                  Payment authorized
                </Text>
                <Text fontSize="13px" fontWeight={600}>
                  We've placed a hold on your card and sent the request. You won't
                  be charged unless the service provider approves it.
                </Text>
              </Box>

              <Text fontSize="18px" fontWeight={800} mb={1}>
                Want to stay informed?
              </Text>
              <Text fontSize="13px" color="gray.500" mb={4}>
                Add the best email for your trip so we can send updates when
                your request is approved or needs attention.
              </Text>

              <Box
                bg="#3e4757"
                border="1px solid"
                borderColor={emailError ? "red.400" : "transparent"}
                borderRadius="14px"
                p={3}
              >
                <Text fontSize="11px" fontWeight={800} color="gray.300" mb={1}>
                  Email address
                </Text>
                <Input
                  variant="unstyled"
                  value={emailValue}
                  onChange={(e) => {
                    setEmailValue(e.target.value);
                    if (emailError) setEmailError("");
                  }}
                  onKeyDown={handleEmailKeyDown}
                  placeholder="you@example.com"
                  color="white"
                  fontSize="15px"
                  fontWeight={600}
                  type="email"
                  autoFocus
                  _placeholder={{ color: "gray.300" }}
                />
              </Box>

              {emailError && (
                <Alert status="error" borderRadius="12px" mt={3}>
                  <AlertIcon />
                  <Text fontSize="13px">{emailError}</Text>
                </Alert>
              )}
            </DrawerBody>

            <DrawerFooter p={3} flexDirection="column" gap={2}>
              <Button
                onClick={handleUpdateEmail}
                isLoading={emailLoading}
                loadingText="Updating..."
                bg="#817efc"
                color="white"
                h="42px"
                w="full"
                variant="solid"
                borderRadius="10px"
                fontSize="14px"
              >
                Update email
              </Button>
              <Button
                onClick={handleUpsellComplete}
                isDisabled={emailLoading}
                variant="ghost"
                h="42px"
                w="full"
                borderRadius="10px"
                fontSize="14px"
              >
                Skip for now
              </Button>
            </DrawerFooter>
          </>
        ) : (
          <>
        <DrawerBody p={3}>
          {/* What's actually being charged. Hour-tier upsells (early check-in /
              late check-out) surface the chosen duration here — this is the
              guest's last chance to catch a wrong tier before the card is held. */}
          {(itemName || itemDuration || itemAmount) && (
            <Box
              bg="whiteAlpha.100"
              border="1px solid"
              borderColor="whiteAlpha.200"
              borderRadius="14px"
              px={3}
              py={3}
              mb={4}
            >
              <Flex align="flex-start" justify="space-between" gap={3}>
                <Box minW={0}>
                  {itemName && (
                    <Text
                      fontSize="14px"
                      fontWeight={700}
                      noOfLines={2}
                      textAlign="left"
                    >
                      {itemName}
                    </Text>
                  )}
                  {itemDuration && (
                    <Text
                      fontSize="12px"
                      fontWeight={600}
                      color="#a5a3ff"
                      mt={0.5}
                      textAlign="left"
                    >
                      {itemDuration}
                    </Text>
                  )}
                </Box>
                {itemAmount && (
                  <Text fontSize="16px" fontWeight={800} whiteSpace="nowrap">
                    {itemAmount}
                  </Text>
                )}
              </Flex>
            </Box>
          )}

          <Text fontWeight={800} fontSize="12px" pb="5px">
            Card Number
          </Text>
          <Box
            px={3}
            py={3}
            borderRadius="md"
            bg="#3e4757"
            mb={4}
            w="full"
          >
            <CardNumberElement
              options={{
                style: {
                  base: {
                    fontSize: "16px",
                    color: "#fff",
                    "::placeholder": { color: "#ccc" },
                    iconColor: "#ccc",
                    fontFamily: "inherit",
                  },
                },
              }}
              onChange={e => setCardComplete(e.complete)}
            />
          </Box>

          <Flex gap={4}>
            <Flex flex={1} flexDirection="column">
              <Text fontWeight={800} fontSize="12px" pb="5px">
                Expiry
              </Text>
              <Box
                px={3}
                py={3}
                borderRadius="md"
                bg="#3e4757"
                w="full"
              >
                <CardExpiryElement
                  options={{
                    style: {
                      base: {
                        fontSize: "16px",
                        color: "#fff",
                        "::placeholder": { color: "#ccc" },
                        fontFamily: "inherit",
                      },
                    },
                  }}
                  onChange={e => setExpiryComplete(e.complete)}
                />
              </Box>
            </Flex>

            <Flex flex={1} flexDirection="column">
              <Text fontWeight={800} fontSize="12px" pb="5px">
                CVC
              </Text>
              <Box
                px={3}
                py={3}
                borderRadius="md"
                bg="#3e4757"
                w="full"
              >
                <CardCvcElement
                  options={{
                    style: {
                      base: {
                        fontSize: "16px",
                        color: "#fff",
                        "::placeholder": { color: "#ccc" },
                        fontFamily: "inherit",
                      },
                    },
                  }}
                  onChange={e => setCvcComplete(e.complete)}
                />
              </Box>
            </Flex>
          </Flex>
        </DrawerBody>

        <DrawerFooter p={3} flexDirection="column" gap={2}>
          <Button
            onClick={handleClick}
            isLoading={loading}
            bg="#817efc"
            color="white"
            h="42px"
            w="full"
            variant="solid"
            borderRadius="10px"
            fontSize="14px"
            disabled={!cardComplete || !expiryComplete || !cvcComplete}
            _disabled={{
              opacity: 0.5,
              cursor: "not-allowed",
              bg: "#817efc",
              color: "white",
              pointerEvents: "none"
            }}
          >
            Pay Now
          </Button>
          <Flex align="center" justify="center" gap={1} opacity={0.45}>
            <FiLock size={11} color="currentColor" />
            <Text fontSize="11px" fontWeight={500} letterSpacing="0.01em">
              Secured by Stripe
            </Text>
          </Flex>
          <CardConsentNote />
        </DrawerFooter>
          </>
        )}
      </DrawerContent>
    </Drawer>
  );
};